import { QueryClient } from '@tanstack/react-query';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { notifications } from '@shared/utils/notifications';
import { sessionExpired } from '@mobx/root';
import i18next from 'i18next';
import { getSessionToken, hasSessionToken } from '@shared/api/session';

/**
 * Русские названия месяцев для подстановки в ошибку STATEMENT_DATE_BEFORE_UNION_CHANGE.
 * Индекс = номер месяца (1-based).
 */
const MONTH_NAMES_RU: Record<number, string> = {
  1: 'январе',
  2: 'феврале',
  3: 'марте',
  4: 'апреле',
  5: 'мае',
  6: 'июне',
  7: 'июле',
  8: 'августе',
  9: 'сентябре',
  10: 'октябре',
  11: 'ноябре',
  12: 'декабре'
};

const MONTH_NAMES_KK: Record<number, string> = {
  1: 'қаңтарда',
  2: 'ақпанда',
  3: 'наурызда',
  4: 'сәуірде',
  5: 'мамырда',
  6: 'маусымда',
  7: 'шілдеде',
  8: 'тамызда',
  9: 'қыркүйекте',
  10: 'қазанда',
  11: 'қарашада',
  12: 'желтоқсанда'
};

/**
 * Обрабатывает структурированную ошибку STATEMENT_DATE_BEFORE_UNION_CHANGE
 * от бэкенда (S3.2 плана СФУ).
 *
 * Формат сообщения от бэкенда:
 *   "STATEMENT_DATE_BEFORE_UNION_CHANGE|changeDt=YYYY-MM-DD|month=N|year=YYYY"
 *
 * @returns true если ошибка была обработана (показан toast), false если нет
 */
const handleStatementDateError = (rawMessage: string): boolean => {
  const SFU_PREFIX = 'STATEMENT_DATE_BEFORE_UNION_CHANGE|';
  if (!rawMessage?.startsWith(SFU_PREFIX)) return false;

  try {
    const paramStr = rawMessage.slice(SFU_PREFIX.length);
    const params: Record<string, string> = {};
    paramStr.split('|').forEach((pair) => {
      const [k, v] = pair.split('=');
      if (k && v) params[k] = v;
    });

    const changeDt = params['changeDt'] ? new Date(params['changeDt']) : null;
    const month = params['month'] ? parseInt(params['month'], 10) : null;

    if (!changeDt || !month) return false;

    const lang = i18next.language ?? 'ru';
    const monthName = lang === 'kk' ? (MONTH_NAMES_KK[month] ?? '') : (MONTH_NAMES_RU[month] ?? '');

    // Форматируем дату dd.MM.yyyy вручную (без date-fns зависимости)
    const dd = String(changeDt.getDate()).padStart(2, '0');
    const mm = String(changeDt.getMonth() + 1).padStart(2, '0');
    const yyyy = changeDt.getFullYear();
    const formattedDate = `${dd}.${mm}.${yyyy}`;

    const msg = i18next.t('registration:errors.statementDateBeforeUnionChange', {
      changeDt: formattedDate,
      monthRu: monthName
    });

    // Показываем через прямой вызов enqueueSnackbar чтобы передать сырую строку
    notifications.toast(msg, 'error');
    return true;
  } catch {
    return false;
  }
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 5
    }
  }
});

const apiUrl = process.env.VITE_REACT_APP_API_URL;

export const AXIOS_INSTANCE = axios.create({
  baseURL: `${apiUrl}/core`,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json; charset=utf-8' }
});

export const AXIOS_DOC_SERVICE_INSTANCE = axios.create({
  baseURL: `${apiUrl}/doc`,
  timeout: 60000
});

/**
 * Микросервис заявок жильцов (oci-requests). Отдельный инстанс, а не общий core-инстанс,
 * по трём причинам, и каждая из них ломала бы работу поодиночке:
 *
 * 1. ДРУГОЙ БАЗОВЫЙ ПУТЬ. nginx проксирует сервис по префиксу `/requests/` — кабинетное API
 *    живёт на `/requests/api/cabinet/`, а не на `/core/api/`.
 * 2. ДРУГОЙ ФОРМАТ УСПЕШНОГО ОТВЕТА. Core заворачивает всё в ApiResponse { code, message, result }
 *    и отдаёт бизнес-ошибки со статусом 200 (code != 0), поэтому у core-инстанса
 *    response-интерцептор возвращает весь конверт. Микросервис отдаёт полезную нагрузку
 *    НАПРЯМУЮ, а об ошибке сообщает HTTP-статусом. Прогонять его ответы через core-интерцептор
 *    нельзя: список заявок `{items:[…]}` тот принял бы за конверт без result.
 * 3. ДРУГОЙ ФОРМАТ ОШИБКИ. В теле ошибки лежит { code, message, result }, где `result` —
 *    МАШИННЫЙ КОД ('request_conflict', 'forbidden', 'validation_error'…). Именно по нему,
 *    а не по подстроке в тексте, различаются ситуации на клиенте.
 */
export const AXIOS_REQUESTS_SERVICE_INSTANCE = axios.create({
  baseURL: `${apiUrl}/requests`,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json; charset=utf-8' }
});

AXIOS_INSTANCE.interceptors.request.use((config) => {
  if (config.url?.indexOf(`/api/Auth`) !== 0) {
    const token = getSessionToken();

    // OSI-796: заголовок ставим ТОЛЬКО при наличии токена. Раньше при пустом токене уходило
    // буквально «Bearer » — заведомо невалидный заголовок и гарантированный 401 у
    // неавторизованного пользователя. Без заголовка бэк тоже ответит 401, но это честное
    // «не авторизован», а не «протухший токен».
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

AXIOS_INSTANCE.interceptors.response.use(
  (response) => {
    const { data } = response;

    try {
      const { result } = data;
      if (response.config.url?.indexOf(`/api/Auth`) === 0 && result && result.token) {
        const { token } = result;
        localStorage.setItem('token', token);
      }
    } catch (err) {
      console.error(err);
    }

    return data;
  },
  (error) => {
    if (error?.response?.status === 401) {
      // OSI-796: тост «Сессия истекла» показываем ТОЛЬКО если сессия реально была (был токен).
      // Если токена не было — 401 означает «пользователь не авторизован», и тост ложный:
      // именно он выскакивал сразу после открытия фронта в новой вкладке без авторизации.
      // sessionExpired() вызываем в любом случае — он и чистит persist, и делает редирект
      // на /auth/check-contact при заходе по прямой ссылке в авторизованную зону.
      if (hasSessionToken()) {
        notifications.warning('Сессия истекла');
      }

      // Оркестратор: authModule.sessionExpired() + очистка persist всех сессионных сторов,
      // иначе старая роль/данные протекают между входами.
      sessionExpired();

      return;
    }

    // Обработка структурированной ошибки СФУ: валидация дат выписки (S3.2)
    const rawMessage: string | undefined =
      error?.response?.data?.message ?? error?.message;
    if (rawMessage && handleStatementDateError(rawMessage)) {
      // Ошибка показана через toast; бросаем чтобы вызывающий код мог остановить spinner
      throw error;
    }

    if (error?.response?.status === 400 && error?.response?.data?.errors) {
      // Собираем сами тексты ошибок валидации (без служебного title и имён полей),
      // чтобы пользователь видел «Указан неверный счет», а не "...occurred.Account: ...".
      // message нигде не парсится (только показывается тостом), fallback на title сохраняет прежнее
      // поведение, если конкретных сообщений нет — другие 400-ошибки не ломаются.
      const messages = Object.values(error.response.data.errors)
        .flatMap((value) => (Array.isArray(value) ? value : [String(value)]))
        .filter(Boolean);
      error.message = messages.length ? messages.join('; ') : error.response.data?.title || error.message;
    }

    // 403 обычно приходит с пустым телом (доступ режется middleware AccessMatrix до контроллера),
    // поэтому error.message остаётся сырым "Request failed with status code 403".
    // Подставляем i18n-КЛЮЧ (notifications.error прогонит его через i18next.t → локализация ru/kk),
    // иначе пользователь видит техническую англ. строку. throw (не return) обязателен:
    // при return await в вызывающем коде резолвится успешно и показывается ложный success-тост.
    if (error?.response?.status === 403) {
      error.message = 'common:forbidden';
    }

    // 413 режет внешний nginx (client_max_body_size) ДО приложения — в ответе прилетает
    // HTML-страница, а не JSON-конверт ApiResponse, поэтому error.message остаётся сырым
    // "Request failed with status code 413". Подставляем i18n-КЛЮЧ (notifications.error /
    // getFileErrorMessage прогонят его через i18next.t), иначе пользователь видит пустую «Ошибка».
    if (error?.response?.status === 413) {
      error.message = 'common:fileTooLargeServer';
    }
    throw error;
  }
);

export const axiosForReactQueryInstance = <T>(config: AxiosRequestConfig, options?: AxiosRequestConfig): Promise<T> => {
  const source = axios.CancelToken.source();
  const promise = AXIOS_INSTANCE({
    ...config,
    ...options,
    cancelToken: source.token
  });

  // @ts-ignore
  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise as Promise<T>;
};

AXIOS_DOC_SERVICE_INSTANCE.interceptors.request.use((config) => {
  const token = getSessionToken();
  // OSI-796: см. комментарий у request-интерцептора AXIOS_INSTANCE — пустой «Bearer » не шлём.
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const axiosForReactQueryDocServiceInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  const source = axios.CancelToken.source();
  const promise = AXIOS_DOC_SERVICE_INSTANCE({
    ...config,
    ...options,
    cancelToken: source.token
  });

  // @ts-ignore
  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise as Promise<T>;
};

AXIOS_REQUESTS_SERVICE_INSTANCE.interceptors.request.use((config) => {
  const token = getSessionToken();
  // Токен тот же, что и у Core: микросервис не проверяет подпись сам, а спрашивает Core
  // (POST /api/internal/auth/introspect). Пустой «Bearer » не шлём — см. AXIOS_INSTANCE.
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

AXIOS_REQUESTS_SERVICE_INSTANCE.interceptors.response.use(
  // Полезная нагрузка приходит без конверта — отдаём тело как есть.
  // Для responseType: 'blob' здесь окажется Blob, и это тоже верно.
  (response) => response.data,
  (error) => {
    if (error?.response?.status === 401) {
      // Ведём себя ровно как core-инстанс: тост только если сессия реально была,
      // затем общий оркестратор разлогина. Расходиться с ним нельзя — иначе протухший
      // токен в одной вкладке даёт два разных сценария в зависимости от того,
      // какой запрос упал первым.
      if (hasSessionToken()) {
        notifications.warning('Сессия истекла');
      }
      sessionExpired();
      return;
    }

    // Текст ошибки написан сервисом ДЛЯ ПОЛЬЗОВАТЕЛЯ (service.DomainError) и безопасен
    // к показу: «Укажите причину отказа», «Заявка уже обработана». Поднимаем его в
    // error.message, иначе наверх ушло бы сырое «Request failed with status code 409».
    const body = error?.response?.data;
    if (body?.message) {
      error.message = body.message;
    }

    // Машинный код ошибки кладём отдельным полем: по нему клиент отличает гонку
    // (request_conflict) от прочих отказов. Разбирать текст сообщения для этого нельзя —
    // формулировку меняют, не думая о фронте.
    if (body?.result) {
      error.requestsErrorCode = body.result;
    }

    // 403 у этого сервиса приходит С ТЕЛОМ и осмысленным текстом (в отличие от Core, где его
    // режет middleware AccessMatrix до контроллера и тело пустое), поэтому подмену на
    // i18n-ключ 'common:forbidden' здесь НЕ делаем — сообщение сервиса точнее.
    if (error?.response?.status === 403 && !body?.message) {
      error.message = 'common:forbidden';
    }

    throw error;
  }
);

export const axiosForReactQueryRequestsServiceInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  const source = axios.CancelToken.source();
  const promise = AXIOS_REQUESTS_SERVICE_INSTANCE({
    ...config,
    ...options,
    cancelToken: source.token
  });

  // @ts-ignore
  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise as Promise<T>;
};

export type ErrorType<Error> = AxiosError<Error>;

export type BodyType<BodyData> = BodyData;
